import { useEffect, useState, useRef } from 'react';

import { Link, useNavigate } from 'react-router';

import { useAuth } from '@clerk/react-router';
import { Trans } from '@lingui/react/macro';
import { motion, useAnimation } from 'framer-motion';
import { ArrowUpRight, CreditCard } from 'lucide-react';

import RecordingModal from './RecordingModal';
import TryForFreeButton from './TryForFreeButton';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { usePostHog } from '~/hooks/usePostHog';

// Utility function to log only on client side
const logOnClient = (level: 'error', message: string, data?: any) => {
  if (typeof window !== 'undefined') {
    console[level](`[Hero] ${message}`, data);
  }
};

interface HeroProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  demo?: React.ReactNode; // Custom demo to replace video
}

const Hero = ({ title, subtitle, demo }: HeroProps = {}) => {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const controls = useAnimation();
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const desktopVideoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const { track } = usePostHog();

  useEffect(() => {
    const startAnimations = async () => {
      try {
        await controls.start({ opacity: 1, y: 0 });
      } catch (error) {
        logOnClient('error', 'Failed to animate Hero component', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    startAnimations();
  }, [controls]);

  const handleStartClick = async () => {
    if (isSignedIn) {
      try {
        await navigate('/practice');
      } catch (error) {
        logOnClient('error', 'Failed to navigate to home', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }
  };

  const handleModalStateChange = (isOpen: boolean) => {
    setIsModalOpen(isOpen);
  };

  const smoothScrollTo = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handlePlayVideo = async () => {
    const mobileVideo = mobileVideoRef.current;
    const desktopVideo = desktopVideoRef.current;
    
    // Determine which video is visible
    const activeVideo = window.innerWidth < 1024 ? mobileVideo : desktopVideo;
    
    if (activeVideo) {
      setShowThumbnail(false);
      
      // Track video play event
      track('welcome-demo-play', {
        video_source: '/videos/welcome.mp4',
        page: 'landing',
        timestamp: new Date().toISOString(),
      });
      
      // Wait for video to be ready if not already
      if (!isVideoReady) {
        await new Promise<void>((resolve) => {
          const handleCanPlayThrough = () => {
            activeVideo.removeEventListener('canplaythrough', handleCanPlayThrough);
            resolve();
          };
          activeVideo.addEventListener('canplaythrough', handleCanPlayThrough);
          
          // Fallback timeout
          setTimeout(() => {
            activeVideo.removeEventListener('canplaythrough', handleCanPlayThrough);
            resolve();
          }, 3000);
        });
      }
      
      // Small delay to ensure sync
      await new Promise(resolve => setTimeout(resolve, 100));
      
      activeVideo.muted = false;
      setIsPlaying(true);
      activeVideo.play().catch(error => {
        logOnClient('error', 'Failed to play video', { error: error.message });
      });
    }
  };

  return (
    <section className="min-h-[85vh] sm:min-h-[700px] flex items-center justify-center px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-gradient-to-br from-blue-50/80 via-[#8B793D]/20 to-[#8B793D]/30 pt-24 sm:pt-28 lg:pt-32 pb-8 sm:pb-12">
      {/* Animated gradient background */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-br from-blue-100/30 via-[#8B793D]/15 to-[#8B793D]/25"
        animate={{
          background: [
            "linear-gradient(135deg, rgba(239,246,255,0.3) 0%, rgba(139,121,61,0.15) 50%, rgba(139,121,61,0.25) 100%)",
            "linear-gradient(135deg, rgba(139,121,61,0.15) 0%, rgba(139,121,61,0.25) 50%, rgba(239,246,255,0.3) 100%)",
            "linear-gradient(135deg, rgba(139,121,61,0.25) 0%, rgba(239,246,255,0.3) 50%, rgba(139,121,61,0.15) 100%)",
            "linear-gradient(135deg, rgba(239,246,255,0.35) 0%, rgba(139,121,61,0.2) 50%, rgba(139,121,61,0.3) 100%)",
            "linear-gradient(135deg, rgba(239,246,255,0.3) 0%, rgba(139,121,61,0.15) 50%, rgba(139,121,61,0.25) 100%)"
          ]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.05)_1px,transparent_0)] bg-[size:20px_20px]" />
      
      {/* Gradient fade to white at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-b from-transparent via-white/60 to-white pointer-events-none" />

      <div className="max-w-[1080px] mx-auto w-full px-4 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-8 items-start lg:items-center">
          {/* First 2 columns - Content */}
          <div className="text-left order-1 lg:order-1 lg:col-span-2">
            <motion.h1
              className="text-5xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-4 sm:mb-5 leading-[1.1]"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              {title || (
                <>
                  <span className="text-brand-dark-blue"><Trans>Your</Trans> </span>
                  <span className="text-brand-gold"><Trans>AI</Trans></span>{' '}
                  <span className="text-brand-dark-blue"><Trans>Speaking Coach,</Trans> </span>
                  <span className="text-brand-gold"><Trans>24/7</Trans></span>
                </>
              )}
            </motion.h1>

            <motion.p
              className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            >
              {subtitle || (
<span className="text-gray-600"><Trans>Pavone helps you practice, analyze, and improve your speaking - before, during, and after every conversation</Trans></span>
              )}
            </motion.p>
        

            {/*
            Speak Confidently
            In Any Situation
            Every Time

            Practice your speaking skills with AI feedback
            */}

            <motion.div
              className="flex flex-col items-stretch sm:items-start gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
            >
              <div className="flex flex-col items-stretch sm:items-start gap-4">
                <div className="flex items-center gap-4">
                  {isSignedIn ? (
                    <Button
                      size="lg"
                      onClick={handleStartClick}
                      className="whitespace-nowrap w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 h-12 sm:h-14 text-base sm:text-lg font-semibold rounded-2xl bg-gradient-to-r from-[#8B793D] via-[#8B793D]/80 to-[#8B793D] text-white shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 relative overflow-hidden group"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-[#8B793D]/60 via-[#8B793D]/40 to-[#8B793D]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                      <span className="relative z-10"><Trans>DASHBOARD →</Trans></span>
                      <div className="absolute inset-0 rounded-2xl bg-white/20 scale-0 group-hover:scale-100 transition-transform duration-300"></div>
                    </Button>
                  ) : (
                    <TryForFreeButton size="lg" showArrow={true} className="whitespace-nowrap" text={<Trans>TRY FOR FREE</Trans>} />
                  )}
                  <span className="hidden sm:flex items-center gap-2 text-sm text-brand-dark-blue whitespace-nowrap">
                    <CreditCard className="w-5 h-5" />
                    <Trans>No credit card needed</Trans>
                  </span>
                </div>
                <a href="/demo" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-6 py-3 h-12 sm:h-14 text-base sm:text-lg font-semibold text-brand-dark-blue border-2 border-brand-dark-blue/30 hover:border-brand-dark-blue rounded-2xl transition-all duration-300">
                  <Trans>View sample report</Trans>
                  <ArrowUpRight className="w-4 h-4 ml-1.5" />
                </a>
              </div>
            </motion.div>

            {/* Video Demo - Mobile Only */}
            {!demo && (
              <motion.div
                className="lg:hidden mt-6 w-full"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
                {/* Native Video Player */}
                <div className="relative aspect-video">
                  <video
                    ref={mobileVideoRef}
                    src="/videos/welcome.mp4"
                    controls
                    muted
                    playsInline
                    preload="auto"
                    className="absolute inset-0 w-full h-full object-contain"
                    onLoadStart={() => setIsVideoLoading(true)}
                    onLoadedMetadata={() => {
                      setIsVideoLoading(false);
                      // Ensure video is ready for sync
                      const video = mobileVideoRef.current;
                      if (video) {
                        video.currentTime = 0;
                      }
                    }}
                    onCanPlay={() => {
                      setIsVideoLoading(false);
                      setVideoError(false);
                    }}
                    onCanPlayThrough={() => {
                      setIsVideoReady(true);
                      setIsVideoLoading(false);
                      setVideoError(false);
                    }}
                    onError={() => {
                      setIsVideoLoading(false);
                      setVideoError(true);
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => {
                      setIsPlaying(false);
                      const video = mobileVideoRef.current;
                      if (video) {
                        video.pause();
                      }
                    }}
                  />
                  
                  {/* Thumbnail Overlay with Play Button */}
                  {showThumbnail && !videoError && (
                    <motion.div
                      className="absolute inset-0 cursor-pointer group"
                      onClick={handlePlayVideo}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {/* Thumbnail Image */}
                      <img 
                        src="/images/welcome_eva.webp" 
                        alt="Video Thumbnail" 
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300">
                        <motion.div
                          className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.5, delay: 0.8 }}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {/* Play Icon */}
                          <svg 
                            className="w-8 h-8 sm:w-10 sm:h-10 text-brand-dark-blue ml-1" 
                            fill="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Loading overlay */}
                  {isVideoLoading && !videoError && !showThumbnail && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                      <div className="relative">
                        <img 
                          src="/images/pavone-mini-white-v2.png" 
                          alt="Loading" 
                          className="w-16 h-16 opacity-70 drop-shadow-sm"
                        />
                        {/* Pulsating ring around logo */}
                        <div className="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping opacity-40"></div>
                        <div className="absolute inset-0 rounded-full border border-blue-300 animate-pulse opacity-60"></div>
                      </div>
                    </div>
                  )}

                  {/* Error state */}
                  {videoError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                      <div className="text-center text-white p-6">
                        <p className="text-sm mb-2"><Trans>Unable to load video</Trans></p>
                        <p className="text-xs text-white/60">
                          <Trans>Place your demo video at:</Trans><br />
                          <code className="bg-white/10 px-2 py-1 rounded mt-2 inline-block">
                            /public/videos/welcome.mov
                          </code>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Frame Overlay - covers 2px of video edges */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 border-[4px] border-white rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </motion.div>
            )}

            {/* Custom Demo - Mobile (if provided) */}
            {demo && (
              <motion.div
                className="lg:hidden mt-6 w-full"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {demo}
              </motion.div>
            )}

            {/* Social Proof */}
            <motion.div
              className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
            >
              {/* User Avatars */}
              <div className="flex -space-x-2">
                <img 
                  src="/images/reviews/review-1-m.webp" 
                  alt="User review" 
                  className="w-8 h-8 rounded-full border-2 border-white object-cover"
                />
                <img 
                  src="/images/reviews/review-2-f.jpg" 
                  alt="User review" 
                  className="w-8 h-8 rounded-full border-2 border-white object-cover"
                />
                <img 
                  src="/images/reviews/review-3-m.jpg" 
                  alt="User review" 
                  className="w-8 h-8 rounded-full border-2 border-white object-cover"
                />
              </div>

              <div className="flex flex-col">
                <div className="flex items-center gap-2 px-2 py-1">
                  <span className="text-sm font-medium text-gray-700"><Trans>Used by</Trans></span>
                  <span className="text-sm font-bold text-brand-dark-blue">34,135+</span>
                  <span className="text-sm font-medium text-gray-700"><Trans>people</Trans></span>
                </div>
                <div className="flex items-center gap-1 text-yellow-400 bg-white rounded-full px-2 py-1">
                  {[...Array(5)].map((_, i) => (
                    // <svg key={i} className="w-4 h-4 text-brand-gold fill-current" viewBox="0 0 24 24">
                    //   <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    // </svg>
                    <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                  <span className="text-sm font-semibold text-brand-dark-blue ml-1">4.7</span>
                  <span className="text-sm text-gray-500">/ 234</span>
                </div>
              </div>
            </motion.div>

            {/* Compliance Badge */}
            {/* <motion.div
              className="mt-4 sm:mt-6 flex items-center gap-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
            >
              <div className="flex items-center gap-2">
                
                <div className="w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <span className="text-sm text-gray-600 font-medium">
                  SOC 2 Type 2 certified and GDPR compliant
                </span>
              </div>
            </motion.div> */}
            
          </div>

          {/* Last 3 columns - Demo (Desktop Only) */}
          <div className="hidden lg:flex relative items-start justify-center order-2 lg:order-2 lg:col-span-3">
            {/* Corner Images - Above demo, positioned at corners */}
            {/* Top Left - Interviews with Green Arrow */}
            {/* <motion.div
              className="absolute bottom-[-24px] right-[-24px] w-28 sm:w-28 md:w-36 z-30"
              initial={{ opacity: 0, x: -10, y: -10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-blue-600/20 rounded-xl blur-md"></div>
                <div className="relative rounded-xl overflow-hidden shadow-lg border border-white">
                  <img 
                    src="/images/academy/compressed/zoom.jpg" 
                    alt="Interviews"
                    className="w-full h-28 sm:h-24 md:h-28 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-1.5 sm:p-2">
                    <span className="text-xs font-bold text-white">Dailys</span>
                  </div>
                </div>
                
              
                <motion.div
                  className="absolute -top-2 -left-2 bg-gradient-to-br from-brand-gold to-brand-gold rounded-full p-1.5 sm:p-2 shadow-lg z-30"
                  animate={{ y: [-3, 3, -3] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                </motion.div>
              </div>
            </motion.div> */}

            {/* Top Right - Sales */}
            {/* <motion.div
              className="absolute top-0 right-0 w-24 sm:w-28 md:w-32 z-30"
              initial={{ opacity: 0, x: 10, y: -10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-green-600/20 rounded-xl blur-md"></div>
                <div className="relative rounded-xl overflow-hidden shadow-lg border border-white">
                  <img 
                    src="/images/academy/compressed/sales.jpg" 
                    alt="Sales"
                    className="w-full h-20 sm:h-24 md:h-28 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-1.5 sm:p-2">
                    <span className="text-xs font-bold text-white">Sales</span>
                  </div>
                </div>
                
           
                <motion.div
                  className="absolute -top-1.5 -right-1.5 bg-gradient-to-br from-brand-green to-brand-green/70 text-white px-1.5 py-0.5 rounded-full text-xs font-bold shadow-md z-30"
                  animate={{ y: [-2, 2, -2] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
                >
                  +34%
                </motion.div>
              </div>
            </motion.div> */}

            {/* Bottom Left - Presentations */}
            {/* <motion.div
              className="absolute bottom-[-64px] left-[-24px] w-24 sm:w-28 md:w-32 z-30"
              initial={{ opacity: 0, x: -10, y: 10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-400/20 to-purple-600/20 rounded-xl blur-md"></div>
                <div className="relative rounded-xl overflow-hidden shadow-lg border border-white">
                  <img 
                    src="/images/academy/compressed/presentation.jpg" 
                    alt="Presentations"
                    className="w-full h-20 sm:h-24 md:h-28 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-1.5 sm:p-2">
                    <span className="text-xs font-bold text-white">Presentations</span>
                  </div>
                </div>
                
              
                <motion.div
                  className="absolute -bottom-1.5 -right-1.5 bg-gradient-to-br from-brand-dark-blue to-brand-dark-blue/70 text-white px-1.5 py-0.5 rounded-full text-xs font-bold shadow-md z-30"
                  animate={{ y: [-2, 2, -2] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
                >
                  92%
                </motion.div>
              </div>
            </motion.div> */}

            {/* Video Demo - Desktop Only */}
            {!demo && (
              <motion.div
                className="hidden lg:block w-full relative z-20 lg:mt-2"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black aspect-video">
                {/* Native Video Player */}
                <div className="relative aspect-video">
                  <video
                    ref={desktopVideoRef}
                    src="/videos/welcome.mp4"
                    controls
                    muted
                    playsInline
                    preload="auto"
                    className="absolute inset-0 w-full h-full object-contain"
                    onLoadStart={() => setIsVideoLoading(true)}
                    onLoadedMetadata={() => {
                      setIsVideoLoading(false);
                      // Ensure video is ready for sync
                      const video = desktopVideoRef.current;
                      if (video) {
                        video.currentTime = 0;
                      }    
                    }}
                    onCanPlay={() => {
                      setIsVideoLoading(false);
                      setVideoError(false);
                    }}
                    onCanPlayThrough={() => {
                      setIsVideoReady(true);
                      setIsVideoLoading(false);
                      setVideoError(false);
                    }}
                    onError={() => {
                      setIsVideoLoading(false);
                      setVideoError(true);
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => {
                      setIsPlaying(false);
                      const video = desktopVideoRef.current;
                      if (video) {
                        video.pause();
                      }
                    }}
                  />
                  
                  {/* Thumbnail Overlay with Play Button */}
                  {showThumbnail && !videoError && (
                    <motion.div
                      className="absolute inset-0 cursor-pointer group"
                      onClick={handlePlayVideo}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      {/* Thumbnail Image */}
                      <img 
                        src="/images/welcome_eva.webp" 
                        alt="Video Thumbnail" 
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300">
                        <motion.div
                          className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.5, delay: 0.8 }}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {/* Play Icon */}
                          <svg 
                            className="w-8 h-8 sm:w-10 sm:h-10 text-brand-dark-blue ml-1" 
                            fill="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Loading overlay */}
                  {isVideoLoading && !videoError && !showThumbnail && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                      <div className="relative">
                        <img 
                          src="/images/pavone-mini-white-v2.png" 
                          alt="Loading" 
                          className="w-16 h-16 opacity-70 drop-shadow-sm"
                        />
                        {/* Pulsating ring around logo */}
                        <div className="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping opacity-40"></div>
                        <div className="absolute inset-0 rounded-full border border-blue-300 animate-pulse opacity-60"></div>
                      </div>
                    </div>
                  )}

                  {/* Error state */}
                  {videoError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                      <div className="text-center text-white p-6">
                        <p className="text-sm mb-2"><Trans>Unable to load video</Trans></p>
                        <p className="text-xs text-white/60">
                          <Trans>Place your demo video at:</Trans><br />
                          <code className="bg-white/10 px-2 py-1 rounded mt-2 inline-block">
                            /public/videos/welcome.mov
                          </code>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Frame Overlay - covers 2px of video edges */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 border-[4px] border-white rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </motion.div>
            )}

            {/* Custom Demo - Desktop (if provided) */}
            {demo && (
              <motion.div
                className="hidden lg:block w-full relative z-20 lg:mt-2"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                {demo}
              </motion.div>
            )}

            {/* COMMENTED OUT: Original Demo - AI Analysis */}
            {/* <motion.div
              className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 border border-gray-200/50 w-full max-w-sm sm:max-w-lg relative z-20"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 bg-red-400 rounded-full"></div>
              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              <div className="ml-auto text-sm text-gray-500">AI Analysis</div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-6">
              <div className="bg-brand-dark-blue/5 rounded-lg p-2 sm:p-3 border border-brand-dark-blue/10">
                <div className="text-xs text-brand-dark-blue mb-1">Overall Score</div>
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <motion.div
                      key={i}
                      className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${i < 4 ? 'bg-brand-gold' : 'bg-gray-200'}`}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.3, delay: i * 0.1 + 1.0 }}
                    />
                  ))}
                </div>
              </div>
              <div className="bg-brand-green/10 rounded-lg p-2 sm:p-3 border border-brand-green/20">
                <div className="text-xs text-brand-green mb-1">Words / min</div>
                <div className="text-base sm:text-lg font-bold text-brand-green">142</div>
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 bg-brand-dark-blue/10 rounded flex items-center justify-center">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-brand-dark-blue rounded-full"></div>
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Speech Analysis</span>
                </div>
                
                <div className="space-y-1 sm:space-y-2 ml-4 sm:ml-6">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Clarity</span>
                    <span className="text-xs font-medium text-brand-dark-blue">Clear</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Pace</span>
                    <span className="text-xs font-medium text-brand-green">Moderate</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Filler Words</span>
                    <span className="text-xs font-medium text-brand-gold">3 detected</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 bg-brand-gold/10 rounded flex items-center justify-center">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-brand-gold rounded-full"></div>
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Video Analysis</span>
                </div>
                
                <div className="space-y-1 sm:space-y-2 ml-4 sm:ml-6">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Eye Contact</span>
                    <div className="w-12 sm:w-16 bg-gray-200 rounded-full h-1">
                      <motion.div 
                        className="bg-brand-gold h-1 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: "78%" }}
                        transition={{ duration: 1.5, delay: 1.8 }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Posture</span>
                    <div className="w-12 sm:w-16 bg-gray-200 rounded-full h-1">
                      <motion.div 
                        className="bg-brand-green h-1 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: "85%" }}
                        transition={{ duration: 1.5, delay: 2.0 }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Gestures</span>
                    <span className="text-xs font-medium text-brand-dark-blue">Natural</span>
                  </div>
                </div>
              </div>

              <div className="bg-brand-green/5 rounded-lg p-2 sm:p-3 border border-brand-green/10">
                <div className="flex items-center gap-2 mb-1 sm:mb-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-brand-green rounded-full"></div>
                  <span className="text-xs font-medium text-gray-700">Improvement Tips</span>
                </div>
                <div className="text-xs text-gray-600 leading-relaxed">
                  • Maintain eye contact for 3-5 seconds at a time
                  <br />
                  • Reduce "um" and "uh" usage by pausing instead
                </div>
              </div>
            </div>
          </div>
          
          <motion.div
            className="absolute -top-4 -right-4 w-8 h-8 bg-blue-400 rounded-full opacity-20"
            animate={{ y: [-10, 10, -10] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
             className="absolute -bottom-2 -left-2 w-6 h-6 bg-purple-400 rounded-full opacity-30"
             animate={{ y: [10, -10, 10] }}
             transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
           />
            </motion.div> */}
          </div>
        </div>
      </div>

      <RecordingModal
        isOpen={isModalOpen}
        onClose={() => handleModalStateChange(false)}
      />
    </section>
  );
};

export default Hero;
